import { createContext, useContext, useEffect, useState } from "react";
import api, { TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=guest, object=user
    const [ready, setReady] = useState(false);

    useEffect(() => {
        // CRITICAL: returning from the Google OAuth callback — AuthCallback exchanges the
        // session_id first, so skip the /auth/me probe here.
        if (window.location.hash?.includes("session_id=")) { setReady(false); return; }
        (async () => {
            if (!localStorage.getItem(TOKEN_KEY)) { setUser(false); setReady(true); return; }
            try {
                const { data } = await api.get("/auth/me");
                setUser(data.user);
            } catch {
                setUser(false);
            } finally {
                setReady(true);
            }
        })();
    }, []);

    const refresh = async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            return data.user;
        } catch { return null; }
    };

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
        return data.user;
    };

    const googleSession = async (sessionId) => {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
        setReady(true);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, ready, login, register, googleSession, logout, refresh, isAdmin: user && user.role === "admin" }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
